import { Dispatch } from 'react';
import { apiClient, apiErrorHandler, ADMIN_URL } from 'src/app/common/network';
import { forEach } from 'lodash';
import { PaginateList } from 'src/app/common/types/common-types';
import { RecruitGenericItem } from '../types/recruit-generic-types';
import {
  ApprovalStatusEnum,
  PersonalParticularItem,
  PersonalParticularDocItem,
  //PHKL Custom -s
  ChangeRequestItem,
  ChangeRequestHistoryItem,
  PdfFileTypeEnum,
  UploadSignedDocAgentInfo,
  UploadSignedDocFileInfo,
  //PHKL Custom -e
} from '../types/personal-particular-types';
//PHKL Custom -s
import { RegionLocale } from 'src/app/i18n';
//PHKL Custom -e

const personalParticularEndpoint = 'recruit-personal-particular';

export type PersonalParticularListParam = {
  appId: string;
  isFollowUpByZone: string;
  page: number;
  limit: number;
};

export type GetDocumentParams = {
  filename: string;
};

export type UpdateApplicationStatusBody = {
  appId: string;
  approvalStatus: ApprovalStatusEnum;
  remarks: string;
  approverCode: string;
};

export type UpdateZoneFollowUpBody = {
  appId: string;
};

export type UploadToCusSearchBody = {
  appId: string;
  fileName: string;
};

//PHKL Custom -s
export type ViewChangeRequestDocumentParam = {
  lang: string;
  appId: string;
  pdfType: PdfFileTypeEnum;
};
export interface CustomDocumentAndUplineAgentResponse {
  customDocumentList?: UploadSignedDocFileInfo[];
  uplineAgentList?: UploadSignedDocAgentInfo[];
}
export interface GetDocumentRequest {
  fileName: string;
  fileType: PdfFileTypeEnum;
}
export interface UploadCustomDocumentBody {
  fileName: string;
  fileType: PdfFileTypeEnum;
  fileData?: string;
}
export interface DeleteCustomDocumentBody {
  fileName: string;
  fileType: PdfFileTypeEnum;
}
export interface GetUplineAgentInfoParams {
  agentCode: string;
  lang?: string;
}
export interface UpdateUplineAgentInfoBody {
  agentCode: string;
}
//PHKL Custom -e

export const fetchPersonalParticularList = async (
  params: PersonalParticularListParam,
  dispatch?: Dispatch<any>,
): Promise<RecruitGenericItem<PaginateList<PersonalParticularItem>>> => {
  let queryPath = `${ADMIN_URL}/${personalParticularEndpoint}?`;
  forEach(params, (param, key) => {
    if (key === 'page') {
      queryPath += `${key}=${encodeURIComponent(param as any)}&`;
    } else {
      queryPath += param ? `${key}=${encodeURIComponent(param as any)}&` : '';
    }
  });
  return apiClient
    .get<RecruitGenericItem<PaginateList<PersonalParticularItem>>>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchPersonalParticularItem = async (
  appId: string,
  dispatch?: Dispatch<any>,
): Promise<RecruitGenericItem<PersonalParticularItem>> => {
  return apiClient
    .get<RecruitGenericItem<PersonalParticularItem>>(`${ADMIN_URL}/${personalParticularEndpoint}/${appId}`)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchDocument = async (
  appId: string,
  params: GetDocumentParams,
  dispatch?: Dispatch<any>,
): Promise<RecruitGenericItem<PersonalParticularDocItem>> => {
  return apiClient
    .get<RecruitGenericItem<PersonalParticularDocItem>>(
      `${ADMIN_URL}/${personalParticularEndpoint}/${appId}/document`,
      { params },
    )
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

//PHKL Custom -s
export const fetchChangeRequestDocument = async (
  params: ViewChangeRequestDocumentParam,
  dispatch?: Dispatch<any>,
): Promise<RecruitGenericItem<PersonalParticularDocItem>> => {
  return apiClient
    .get<RecruitGenericItem<PersonalParticularDocItem>>(
      `${ADMIN_URL}/${personalParticularEndpoint}/${params.appId}/changeRequestDocument`,
      { params: { ...params, lang: 'en' } },
    )
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};
//PHKL Custom -e

export const fetchDocumentNameList = async (
  appId: string,
  dispatch?: Dispatch<any>,
): Promise<RecruitGenericItem<string[]>> => {
  return apiClient
    .get<RecruitGenericItem<string[]>>(`${ADMIN_URL}/${personalParticularEndpoint}/${appId}/documentNameList`)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const updateApplicationStatus = async (
  body: UpdateApplicationStatusBody,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  return apiClient
    .put<void>(`${ADMIN_URL}/${personalParticularEndpoint}/updateStatus`, body)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const updateZoneFollowUp = async (body: UpdateZoneFollowUpBody, dispatch?: Dispatch<any>): Promise<void> => {
  return apiClient
    .put<void>(`${ADMIN_URL}/${personalParticularEndpoint}/updateZoneFollowUp`, body)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const uploadToCusSearch = async (body: UploadToCusSearchBody, dispatch?: Dispatch<any>): Promise<void> => {
  return apiClient
    .put<void>(`${ADMIN_URL}/${personalParticularEndpoint}/uploadToCusSearch`, body)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

//PHKL Custom -s
export const fetchChangeRequestList = async (
  appId: string,
  dispatch?: Dispatch<any>,
): Promise<RecruitGenericItem<ChangeRequestItem[]>> => {
  const url = `${ADMIN_URL}/${personalParticularEndpoint}/${appId}/changeRequest`;
  return apiClient
    .get<RecruitGenericItem<ChangeRequestItem[]>>(url)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const updateChangeRequestStatus = async (
  appId: string,
  action: 'approved' | 'rejected',
  item: ChangeRequestItem,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  const body = {
    appId: appId,
    action: action,
    section: item.section,
    sessionId: item.sessionId,
  };
  return apiClient
    .post<void>(`${ADMIN_URL}/${personalParticularEndpoint}/${appId}/changeRequest`, body)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const getChangeRequestHistoryList = async (
  appId: string,
  dispatch?: Dispatch<any>,
): Promise<RecruitGenericItem<ChangeRequestHistoryItem[]>> => {
  return apiClient
    .get<RecruitGenericItem<ChangeRequestHistoryItem[]>>(
      `${ADMIN_URL}/${personalParticularEndpoint}/${appId}/changeRequestHistory`,
    )
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const getCustomDocumentAndUplineAgent = async (
  appId: string,
  lang?: string,
  dispatch?: Dispatch<any>,
): Promise<RecruitGenericItem<CustomDocumentAndUplineAgentResponse>> => {
  const url = `${ADMIN_URL}/${personalParticularEndpoint}/${appId}/customDocumentAndUplineAgent`;
  return apiClient
    .get<RecruitGenericItem<CustomDocumentAndUplineAgentResponse>>(url, {
      params: { lang: 'en' },
    })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const uploadCustomDocument = async (appId: string, body: UploadCustomDocumentBody): Promise<void> => {
  return apiClient
    .post(`${ADMIN_URL}/${personalParticularEndpoint}/${appId}/uploadCustomDocument`, body)
    .then((response) => response.data);
};

export const deleteCustomDocument = async (
  appId: string,
  data: DeleteCustomDocumentBody,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  return apiClient
    .delete(`${ADMIN_URL}/${personalParticularEndpoint}/${appId}/deleteCustomDocument`, { data })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const getUplineAgentInfo = async (
  appId: string,
  params: GetUplineAgentInfoParams,
): Promise<RecruitGenericItem<UploadSignedDocAgentInfo>> => {
  return apiClient
    .get<RecruitGenericItem<UploadSignedDocAgentInfo>>(
      `${ADMIN_URL}/${personalParticularEndpoint}/${appId}/uplineAgentInfo`,
      { params: { ...params, lang: 'en' } },
    )
    .then((response) => response.data);
};

export const addUplineAgentInfo = async (
  appId: string,
  body: UpdateUplineAgentInfoBody,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  return apiClient
    .put(`${ADMIN_URL}/${personalParticularEndpoint}/${appId}/uplineAgentInfo`, body)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const deleteUplineAgentInfo = async (
  appId: string,
  data: UpdateUplineAgentInfoBody,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  return apiClient
    .delete(`${ADMIN_URL}/${personalParticularEndpoint}/${appId}/uplineAgentInfo`, { data })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};
//PHKL Custom -e
